import { IconSearch, IconX } from '@tabler/icons-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Div } from 'common/styles/oc-styled-components';
import { DIMENSIONS, useWindowDimensions } from 'providers/WindowDimensionsProvider';
import { useEffectIf } from 'utils/customHooks/useEffectIf';
import Logger from 'utils/Logger';

import { isMobileWebKit } from '../../../../utils/useragent';

import { DropdownParent, FloatingStyledInput } from './Autocomplete-styled-components';
import { Dropdown } from './Dropdown';
import { DIRECTION } from './types';

const MAX_DROPDOWN_HEIGHT = 300;

const getVisibleHeight = () =>
  window.visualViewport?.height || window.innerHeight;

// Option is valid if no filter or if the option name includes the filter
// Exclude labels and disabled options when filtering
const filterMatches = (o: AutocompleteOption, filter: string, filterBy: AutocompleteProps['filterBy']) => {
  if (!filter) {
    return true;
  }

  if (o.isLabel || o.isDisabled) {
    return false;
  }

  return filterBy!.some((field) => o[field as keyof AutocompleteOption]?.toLowerCase().includes(filter.toLowerCase()));
};

export interface AutocompleteOption {
  id: any;
  name: string;
  isDisabled: boolean;
  isLabel: boolean;
}

interface AutocompleteProps {
  preferredDropDirection?: DIRECTION;
  shouldAutoFocus?: boolean;
  autoSelectOnBlur?: boolean;
  defaultSelected: AutocompleteOption['id'] | AutocompleteOption['name'] | AutocompleteOption;
  onSelect: (optionId: AutocompleteOption['id']) => void;
  options: AutocompleteOption[];
  filterBy?: [AutocompleteOption['id']?, AutocompleteOption['name']?];
  testid?: string;
  placeholderText?: string;
  className?: string;
  isRelative?: boolean;
  isSensitive?: boolean;
  showSuffix?: boolean;
  style?: React.CSSProperties;
}

export const Autocomplete = ({
  preferredDropDirection,
  shouldAutoFocus,
  autoSelectOnBlur = false,
  defaultSelected,
  onSelect,
  options,
  filterBy = ['name'],
  testid,
  placeholderText,
  // Allows custom styling by styled-components
  className: styledComponentClassName,
  // isRelative true allows us to use the dropdown parent as the attachment point for the dropdown menu
  isRelative = false,
  isSensitive = true,
  showSuffix = true,
  style,
}: AutocompleteProps) => {
  // Prefer using parent's state, but allow internal state to be set so we don't have to wait for parent to update
  const [internalSelectedOption, setInternalSelectedOption] = useState<AutocompleteOption['id'] | null>(null);
  const selectedOption = useMemo(() => {
    return defaultSelected ?? internalSelectedOption;
  }, [internalSelectedOption, defaultSelected]);

  const callOnSelect = useCallback((optionId: AutocompleteOption['id']) => {
    setInternalSelectedOption(optionId);
    onSelect(optionId);
  }, [onSelect]);

  // Make sure we clear the filter and selected option if options are removed
  useEffect(() => {
    if (!options.length) {
      setFilter(null);
      callOnSelect(null);
    }
  }, [options, callOnSelect]);

  const { layout } = useWindowDimensions();

  const wrapperRef = useRef<HTMLDivElement>(null);
  const dropdownParentRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const dropdownParentRect = (() => {
    if (!dropdownParentRef.current) {
      return null;
    }
    return dropdownParentRef.current.getBoundingClientRect();
  })();

  const inputRect = (() => {
    if (!inputRef.current) {
      return null;
    }
    return inputRef.current.getBoundingClientRect();
  })();

  const inputHeight = useMemo(() => {
    if (!inputRect) {
    // Reasonable default
      return 65;
    }

    const { height } = inputRect;
    return height;
  }, [inputRect]);

  // Should be no taller than the top of the screen minus the size of the input
  const maxDropdownHeight = useMemo(() =>
    Math.min(getVisibleHeight() - inputHeight, MAX_DROPDOWN_HEIGHT), [inputHeight]);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropDirection, setDropDirection] = useState(preferredDropDirection);
  const [filter, setFilter] = useState<string | null>(null);
  const [hoveredOptionId, setHoveredOptionId] = useState(null);
  const [isMouseActive, setIsMouseActive] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);

  useEffect(() => {
    if (!isScrolling) {
      return;
    }
    setIsScrolling(false);
  }, [isScrolling]);

  const scrollToInput = useCallback(
    () => {
      // Only scroll on mobile and tablet
      if (
        !dropdownOpen
        || !wrapperRef.current
        || ![DIMENSIONS.MOBILE, DIMENSIONS.TABLET].includes(layout)
      ) {
        return;
      }

      const scrollAmount =
        wrapperRef.current.getBoundingClientRect().bottom - getVisibleHeight();

      window.scrollTo({
        top: document.documentElement.scrollTop + scrollAmount,
        behavior: 'smooth',
      });
      setIsScrolling(true);
    },
    [dropdownOpen, layout],
  );

  // Dynamically set the dropdown direction based on available space
  const calculateAndSetDropDirection = useCallback(() => {
    if (!dropdownParentRect || !inputRect) {
      return;
    }

    const { top: inputTop, bottom: inputBottom } = inputRect;

    const visibleHeight = getVisibleHeight();
    // Calculate the space above and below the input
    const spaceAbove = inputTop;
    const spaceBelow = visibleHeight - inputBottom;

    // If a direction is preferred and there's enough space for it, use it
    if (preferredDropDirection) {
      const isPreferredDirectionValid = {
        [DIRECTION.DOWN]: dropdownParentRect.top >= 0,
        [DIRECTION.UP]: dropdownParentRect.bottom <= visibleHeight,
      }[preferredDropDirection];

      if (isPreferredDirectionValid) {
        setDropDirection(preferredDropDirection);
        return;
      }
    }

    // Otherwise (no direction requested or it doesn't fit), use the direction with more space
    const direction =
      spaceBelow > spaceAbove ? DIRECTION.DOWN : DIRECTION.UP;
    setDropDirection(direction);
  }, [dropdownParentRect, inputRect, preferredDropDirection]);

  const filteredOptions = useMemo(() => {
    if (!filter) {
      return options;
    }
    return options.filter((o) => filterMatches(o, filter, filterBy));
  }, [options, filter, filterBy]);

  const updateViewportAndDirection = useCallback(() => {
    scrollToInput();
    calculateAndSetDropDirection();
  }, [scrollToInput, calculateAndSetDropDirection]);

  useEffect(() => {
    if (dropdownOpen) {
      updateViewportAndDirection();
    }
  }, [dropdownOpen, updateViewportAndDirection]);

  useEffect(() => {
    if (window.visualViewport !== null && window.visualViewport !== undefined) {
      window.visualViewport.addEventListener(
        'resize',
        updateViewportAndDirection,
      );

      return () => window.visualViewport?.removeEventListener(
        'resize',
        updateViewportAndDirection,
      );
    }

    // If visualViewport is not supported, fall back to window resize
    window.addEventListener('resize', updateViewportAndDirection);
    return () => window.removeEventListener('resize', updateViewportAndDirection);
  }, [updateViewportAndDirection]);

  // On legal keyboard input, mouse is inactive (handled in handleKeyDown)
  // On any mouse input, mouse is active (handled here)
  useEffect(() => {
    const handleSetMouseActive = () => setIsMouseActive(true);
    const events = ['mousedown', 'mouseup', 'mousemove'];

    events.forEach((event) => {
      document.addEventListener(event, handleSetMouseActive);
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleSetMouseActive);
      });
    };
  }, []);

  // if the user's mouse is inactive, we don't want to count onmouseenter events that trigger from keyboard navigation
  const handleMouseEnter = (id: AutocompleteOption['id']) => {
    if (!isMouseActive) {
      return;
    }
    setHoveredOptionId(id);
  };

  const handleFocus = () => {
    setDropdownOpen(true);
  };

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    // If the blur was because of outside focus, close the dropdown
    if (event.currentTarget.contains(event.relatedTarget)) {
      return;
    }

    // Auto-select last hovered option when dropdown closes
    if (hoveredOptionId && autoSelectOnBlur) {
      callOnSelect(hoveredOptionId);
    }
    setDropdownOpen(false);
  };

  useEffect(() => {
    // filter is `null` when the user has never typed anything, in which case we don't want to change the selected option
    if (dropdownOpen || filter === null || !options.length) {
      return;
    }
    if (filter?.length > 0) {
      const defaultOption = options.find((o) => [o, o.id, o.name].includes(selectedOption));
      setFilter(defaultOption?.name ?? null);
      return;
    }

    // If dropdown is closed while filter is empty, deselect the option
    callOnSelect(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we don't want to re-run this effect when onSelect changes
  }, [filter, dropdownOpen, selectedOption, options]);

  const handleSelect = (optionId: AutocompleteOption['id']) => {
    if (!optionId) {
      return;
    }
    const selectedOption = options.find((o) => o.id === optionId);
    if (!selectedOption) {
      return;
    }
    setFilter(selectedOption.name);
    callOnSelect(selectedOption.id);
    setDropdownOpen(false);
  };

  const isElementVisible = (element: Element) => {
    if (!element || !element?.parentElement) {
      return false;
    }

    const { parentElement } = element;

    const {
      top,
      bottom,
    } = element.getBoundingClientRect();

    const {
      top: parentTop,
      bottom: parentBottom,
    } = parentElement.getBoundingClientRect();

    const isTopVisible = top >= parentTop;
    const isBottomVisible = bottom <= parentBottom;
    const isVisible = isTopVisible && isBottomVisible;

    return isVisible;
  };

  const scrollToOption = (id: AutocompleteOption['id']) => {
    const optionElement = document.querySelector(`[data-option-id="${id}"]`);

    if (optionElement && !isElementVisible(optionElement)) {
      optionElement.scrollIntoView({
        behavior: 'instant',
        block: 'nearest',
      });
    }
  };

  const findFirstValidIndex = (list: AutocompleteOption[], direction: DIRECTION) =>
    direction === DIRECTION.DOWN
      ? list.findIndex((o) => !o.isDisabled && !o.isLabel)
      : list.findLastIndex((o) => !o.isDisabled && !o.isLabel);

  // Get the next valid option index based on the given direction (up/down)
  const getNextHoverableOptionIndex = (direction = DIRECTION.DOWN) => {
    // This should never happen, but in case it does, default to ascending and log an error
    if (!Object.values(DIRECTION).includes(direction)) {
      Logger.error('Invalid direction', { direction });

      direction = DIRECTION.DOWN;
    }

    const currentIndex = filteredOptions
      .map((o) => o.id)
      .indexOf(hoveredOptionId);

    const searchStartIndex = {
      [DIRECTION.UP]: 0,
      [DIRECTION.DOWN]: currentIndex + 1,
    }[direction];

    const searchEndIndex = {
      [DIRECTION.UP]: currentIndex,
      [DIRECTION.DOWN]: filteredOptions.length,
    }[direction];

    const searchOptions = filteredOptions.slice(
      searchStartIndex,
      searchEndIndex,
    );

    let nextIndex = findFirstValidIndex(searchOptions, direction);
    // If we didn't find a valid option, loop back to the first/last valid option
    if (nextIndex === -1) {
      nextIndex = findFirstValidIndex(filteredOptions, direction);
    } else {
      nextIndex += searchStartIndex;
    }

    return nextIndex;
  };

  const moveHover = (direction: DIRECTION) => {
    const nextIndex = getNextHoverableOptionIndex(direction);
    const nextOption = filteredOptions[nextIndex];

    // Should only happen if no results are found
    if (!nextOption?.id) {
      return;
    }
    setHoveredOptionId(nextOption.id);
    scrollToOption(nextOption.id);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!dropdownOpen) {
      return;
    }

    const handle = {
      ArrowUp: () => moveHover(DIRECTION.UP),
      ArrowDown: () => moveHover(DIRECTION.DOWN),
      Enter: () => {
        e.preventDefault(); // prevent enter from submitting form
        handleSelect(hoveredOptionId);
      },
    }[e.key];

    if (handle) {
      setIsMouseActive(false);
      handle();
    }
  };

  const handleSetFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFilter = e.target.value;
    setFilter(newFilter);
  };

  // Set the filter to the selected option's name
  // Matches on id, name, or the option itself
  useEffect(() => {
    const defaultOption = options.find((o) => [o, o.id, o.name].includes(selectedOption));
    setFilter(defaultOption?.name ?? null);
  }, [selectedOption, options]);

  const findFirstValidOptionId = () => {
    const firstValidIndex = findFirstValidIndex(
      filteredOptions,
      DIRECTION.DOWN,
    );
    return filteredOptions[firstValidIndex || 0]?.id;
  };

  // If nothing is hovered or if the hovered option is no longer in the list, highlight the first option instead
  useEffectIf({
    condition: !filteredOptions.map((o) => o.id).includes(hoveredOptionId),
    effect: () => setHoveredOptionId(findFirstValidOptionId()),
    watchList: [filteredOptions],
  });

  // Focus the input when the component mounts
  // For some reason, this needs a timeout to work
  useEffect(() => {
    if (
      !wrapperRef.current
      || !shouldAutoFocus
      // do not autofocus on mobile webkit browsers (keyboard doesn't open on focus)
      || isMobileWebKit()
    ) {
      return () => {};
    }

    const focusTimeout = setTimeout(() => {
      const textInput = wrapperRef.current?.querySelector('input');
      if (textInput) {
        textInput.focus();
      }
    });
    return () => clearTimeout(focusTimeout);
  }, [wrapperRef, shouldAutoFocus]);

  // Search icon if filter is empty, else clear button
  const InputSuffix = useMemo(() => {
    if (!showSuffix) {
      return null;
    }

    const iconProps = {
      size: 16,
    };

    if (!filter) {
      return <IconSearch {...iconProps} />;
    }

    return (
      <IconX
        {...iconProps}
        role="button"
        data-testid="clear-button"
        onClick={(e) => {
          // Prevent briefly openingthe dropdown
          e.preventDefault();
          setFilter('');
          callOnSelect(null);
        }}
        style={{ cursor: 'pointer' }}
      />
    );
  }, [filter, callOnSelect, showSuffix]);

  const sensitiveClassName = isSensitive ? 'sensitive-data' : '';

  return (
    <Div
      ref={wrapperRef}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={`${styledComponentClassName || ''} ${sensitiveClassName}`}
      position={isRelative ? 'relative' : 'initial'}
    >
      <div ref={inputRef}> {/* This div exists only to get the input's bounding client rect */}
        <FloatingStyledInput
          style={style}
          placeholder={placeholderText || 'Search...'}
          className="autocomplete-search-input"
          disabled={!options?.length}
          value={filter}
          onChange={handleSetFilter}
          suffix={InputSuffix}
          data-testid={testid}
        />
      </div>

      <DropdownParent
        ref={dropdownParentRef}
        $maxHeight={maxDropdownHeight}
        $dropDirection={dropDirection}
        $shouldShow={dropdownOpen}
        $center={filteredOptions?.length < 2}
      >
        <Dropdown
          options={filteredOptions}
          filter={filter}
          hoveredOption={hoveredOptionId}
          handleMouseEnter={handleMouseEnter}
          handleSelect={handleSelect}
        />
      </DropdownParent>
    </Div>
  );
};
